// Tender cost model — the operating-cost build-up behind a price submission.
//
// The question this answers is the one the tender actually asks: what does it
// cost us to run the contract for a year, and what must each of the four priced
// lines charge so that the whole thing lands on Australia Post's recognised
// profit of 5%?
//
// Everything here is pure arithmetic on one plain `model` object, so the numbers
// can be checked in Node without a browser (transform with @babel/core preset-env
// and _compile it, the same way peakPlan.js is checked).
//
// Three ideas hold the model together:
//
//   1. COST IS BUILT PER DRIVER-DAY AND PER VAN-YEAR, not per parcel. Labour and
//      parking follow the driver; finance, fuel, tyres and servicing follow the
//      van. A parcel rate is what falls out at the end, never what goes in.
//
//   2. PROFIT IS A MARGIN ON REVENUE, not a mark-up on cost. AP's "5%" means five
//      cents in every dollar invoiced, so required revenue = cost / (1 − 0.05),
//      which is a 5.26% mark-up. Both figures are reported so nobody has to guess
//      which one a number is.
//
//   3. PRICES MOVE TOGETHER UNLESS YOU SAY OTHERWISE. Each line carries a weight;
//      by default the weights are the current AP rates themselves, so the answer
//      comes back as one uplift percentage applied evenly across all four lines.
//      Change a weight to load one line harder than another.
//
// Rates and volumes seeded here are the live ones the app already bills on — see
// DEFAULTS for where each came from.

// ── Award ────────────────────────────────────────────────────────────────────
// Road Transport and Distribution Award 2020 [MA000038], Grade 2, from 1 Jul 2026
// (2025-26 base 26.27 + the FWC's 4.75% — the same arithmetic as fair-work-rates.js).
export const GRADE2_BASE_2026 = 27.52;

// ── Vehicle profiles ─────────────────────────────────────────────────────────
// TWO options priced side by side. Every figure is an ASSUMPTION until a dealer
// quote and a fleet-insurance quote replace it — that is why they are all editable
// on the page. The consumption figures are the manufacturers' combined-cycle ADR
// numbers, which is exactly why the fuel section applies a stop-start uplift to
// them: nobody delivering the Sydney CBD ever sees an ADR figure.
export const VEHICLE_PRESETS = {
  hiace: {
    key: 'hiace',
    label: 'Toyota HiAce LWB 2.8 TD',
    note: '2t van · the fleet standard (16 in service today)',
    capex: 52000,            // drive-away, incl. on-roads and fit-out
    residualPct: 45,         // % of capex at end of term
    l100: 8.2,               // ADR combined
    serviceIntervalKm: 10000,
    serviceIntervalMonths: 6,
    serviceCost: 290,        // capped-price servicing
    tyreSetCost: 900,
    tyreLifeKm: 60000,
    regoCtp: 1150,
    insurance: 3100,
    repairs: 700,            // unscheduled repairs + wear items, per van per year
  },
  deliver9: {
    key: 'deliver9',
    label: 'LDV Deliver 9 LWB 2.0 TD',
    note: 'the cheaper option · 14 already in service',
    capex: 45000,
    residualPct: 33,         // holds value less well than the Toyota — the catch
    l100: 9.0,
    serviceIntervalKm: 15000,
    serviceIntervalMonths: 12,
    serviceCost: 450,
    tyreSetCost: 750,
    tyreLifeKm: 55000,
    regoCtp: 1150,
    insurance: 3100,
    repairs: 950,            // higher unscheduled spend is the trade for the capex
  },
};

// ── The four priced lines ────────────────────────────────────────────────────
// Units differ line to line and that matters: parcels and Express are per article,
// depot work is per bag, and DoE is a DAY rate plus per-school collections. Pricing
// DoE per item would be pricing the wrong thing.
export const LINE_DEFS = [
  { key: 'parcels', label: 'Parcels',          unit: 'article',     hint: 'Ordinary + Express + Signature + eParcel, all at the one AP rate' },
  { key: 'depot',   label: 'Depot Bags',       unit: 'bag',         hint: 'AP Drop Rate' },
  { key: 'doe',     label: 'DoE',              unit: 'service day', hint: 'DET service day rate + per-school collections' },
  { key: 'express', label: '2nd Wave Express', unit: 'item',        hint: 'unit price less the per-item adjustment — we price the net' },
];

// ── Defaults ─────────────────────────────────────────────────────────────────
// Live figures, dated 9 Sep 2026, so a first load is already a real test run:
//   volumes     — wahr_history, the 12 months to 31 Aug 2026: 1,892,490 articles over
//                 257 delivery days (252 weekdays, 5 Saturdays). Depot bags and Express
//                 are carried across those days from the months their feeds cover.
//   driver-days — driver_logs, Aug 2026: 44.5 logins a day, 8.1 h a shift, 25 km a shift
//   rates       — the latest AP RCTI + the DoE / EP tax invoices the app generates
export function defaultModel() {
  return {
    version: 1,
    name: 'Test run — City Contract',
    notes: '',

    margin: { pct: 5, basis: 'revenue' },     // AP's recognised profit

    ops: {
      // The delivery calendar as actually worked, not as assumed. wahr_history for
      // the 12 months to 31 Aug 2026 holds 257 delivery days — 252 weekdays and just
      // FIVE Saturdays. This is a Monday-to-Friday operation with occasional Saturday
      // work, and costing it as Mon–Sat added a phantom 47 days of drivers and vans.
      weekdayDays: 252,
      saturdayDays: 5,
      driversPerDay: 44.5,       // live: driver_logs, Aug 2026
      satDriversPerDay: 12,
      hoursPerShift: 8.1,        // live: median 8.29, mean 8.10
      otHoursPerShift: 0,
      satHoursPerShift: 6,
    },

    labour: {
      baseRate: GRADE2_BASE_2026,
      casualLoadingPct: 25,
      satPenaltyPct: 50,
      otFirst2Pct: 50,
      otAfterPct: 100,
      casualOtLoadingPct: 10,    // replaces the 25% casual loading during overtime
      superPct: 12,
      workersCompPct: 5.5,       // icare NSW road freight — CONFIRM against the policy
      payrollTaxPct: 5.45,       // NSW
      payrollTaxOn: true,
      payrollTaxThreshold: 1200000,
      allowancePerShift: 0,
    },

    fleet: {
      vehicle: 'hiace',
      vans: 48,
      spareVans: 4,              // held for swaps and services; cost, no driver
      ownership: 'finance',      // 'finance' | 'lease'
      financeRatePct: 8.5,
      termYears: 5,
      leasePerVanPerMonth: 1100,
      profiles: {
        hiace: { ...VEHICLE_PRESETS.hiace },
        deliver9: { ...VEHICLE_PRESETS.deliver9 },
      },
    },

    fuel: {
      kmPerDay: 25,              // live: driver_logs Aug 2026, mean 25 / median 20
      stopStartUpliftPct: 65,    // CBD stop-start, kerb-to-door, cold engine all day
      idleHoursPerDay: 1.5,      // queues, loading, traffic — engine on, wheels still
      idleLitresPerHour: 0.9,
      dieselPerLitre: 2.05,
    },

    equipment: {
      mobilePerMonth: 35,        // per driver
      trolleyCost: 450,
      trolleyLifeYears: 3,
      parkingTollsPerDay: 18,    // per driver-day
      finesPerVanPerYear: 250,   // unrecovered infringements
      rows: [
        { label: 'Uniform & PPE', perDriverPerYear: 180 },
        { label: 'Scanner / handheld consumables', perDriverPerYear: 60 },
      ],
    },

    overheads: {
      rows: [
        { label: 'Management & administration', annual: 260000 },
        { label: 'Depot, office & utilities', annual: 60000 },
        { label: 'Public liability & goods-in-transit insurance', annual: 24000 },
        { label: 'Accounting, legal & compliance', annual: 36000 },
        { label: 'Software, IT & telematics', annual: 24000 },
      ],
    },

    // Volumes are ANNUAL, because the tender prices a year. A daily rate multiplied
    // by an assumed number of operating days is where a forecast goes wrong: it did
    // here, until the delivery calendar was read off wahr_history instead of assumed.
    lines: {
      // Parcels = everything AP pays the 310.94c article rate on. The WAHR total is
      // all delivered articles, Express included — and Express is then paid a SECOND
      // time on the 2nd Wave line, which is why it appears in both.
      parcels: { volPerYear: 1892490, currentRate: 3.1094, weight: 3.1094, floorAtCurrent: true, include: true },
      depot:   { volPerYear: 19334,   currentRate: 7.3794, weight: 7.3794, floorAtCurrent: true, include: true },
      // 20 service days a period × 13, and 9 schools × 8 collections a period — read
      // off the DoE tax invoice actually raised ($5,408.23 ex for the period to 8 Aug
      // 2026), not off the 12-school figure the invoice template still defaults to.
      // Rates: the Oct 2026 fee increase (Tax Invoices → Rates tab).
      doe:     { volPerYear: 260, currentRate: 262.9273, weight: 262.9273, floorAtCurrent: true, include: true,
                 collectionsPerYear: 936, collectionRate: 5.3208 },
      express: { volPerYear: 18858, currentRate: 3.43, weight: 3.43, floorAtCurrent: true, include: true,
                 grossRate: 5.91, adjustment: -2.48 },
    },

    // Revenue the depot earns that these drivers and vans do NOT deliver — Night Sort
    // and additional time are done by sorters on a lump-sum variation. It is not
    // priced here and its cost is not in the cost base; it sits on the page only so
    // the total can be reconciled against the company's actual turnover.
    otherRevenue: { label: 'Night Sort & additional time', annual: 0 },
  };
}

// ── Helpers ──────────────────────────────────────────────────────────────────
const n = (v, d = 0) => { const x = Number(v); return Number.isFinite(x) ? x : d; };
const pc = (v) => n(v) / 100;

// ── Labour ───────────────────────────────────────────────────────────────────
// Casual Grade 2. Ordinary hours carry the 25% casual loading; overtime drops that
// loading for the award's 10% casual overtime loading and adds the penalty on top
// (cl 11.2 / 21). Saturday is a 50% penalty on the casual rate. Penalties never
// compound — the higher one applies.
export function labourRates(l) {
  const base = n(l.baseRate);
  const casual = base * (1 + pc(l.casualLoadingPct));
  return {
    base,
    casual,
    saturday: base * (1 + pc(l.casualLoadingPct) + pc(l.satPenaltyPct)),
    otFirst2: base * (1 + pc(l.casualOtLoadingPct) + pc(l.otFirst2Pct)),
    otAfter: base * (1 + pc(l.casualOtLoadingPct) + pc(l.otAfterPct)),
  };
}

// What one shift costs in wages, before on-costs.
export function shiftCost(l, ordinaryHours, otHours, rates) {
  const r = rates || labourRates(l);
  const ot = Math.max(0, n(otHours));
  const first2 = Math.min(2, ot);
  return n(ordinaryHours) * r.casual
       + first2 * r.otFirst2
       + (ot - first2) * r.otAfter
       + n(l.allowancePerShift);
}

export function computeLabour(model) {
  const { ops, labour: l } = model;
  const rates = labourRates(l);

  const weekdayDriverDays = n(ops.driversPerDay) * n(ops.weekdayDays);
  const satDriverDays = n(ops.satDriversPerDay) * n(ops.saturdayDays);
  const driverDays = weekdayDriverDays + satDriverDays;

  const weekdayShift = shiftCost(l, ops.hoursPerShift, ops.otHoursPerShift, rates);
  const satShift = n(ops.satHoursPerShift) * rates.saturday + n(l.allowancePerShift);

  const wages = weekdayDriverDays * weekdayShift + satDriverDays * satShift;
  const superAmt = wages * pc(l.superPct);
  // icare counts superannuation as wages, so workers comp sits on wages + super.
  const workersComp = (wages + superAmt) * pc(l.workersCompPct);
  const payrollBase = wages + superAmt;
  const payrollTax = l.payrollTaxOn
    ? Math.max(0, payrollBase - n(l.payrollTaxThreshold)) * pc(l.payrollTaxPct)
    : 0;

  const total = wages + superAmt + workersComp + payrollTax;
  const hoursPerYear = weekdayDriverDays * (n(ops.hoursPerShift) + n(ops.otHoursPerShift))
                     + satDriverDays * n(ops.satHoursPerShift);

  return {
    rates, driverDays, weekdayDriverDays, satDriverDays,
    weekdayShift, satShift, hoursPerYear,
    wages, super: superAmt, workersComp, payrollTax, total,
    perDriverDay: driverDays ? total / driverDays : 0,
    effectiveHourly: hoursPerYear ? total / hoursPerYear : 0,
  };
}

// ── Vehicles ─────────────────────────────────────────────────────────────────
// Ownership is straight-line depreciation plus interest on the average balance —
// the build-up a tender panel can follow, rather than a lease payment that hides
// both. A lease alternative is a single figure per van per month.
export function computeVehicle(model, vehicleKey) {
  const f = model.fleet;
  const key = vehicleKey || f.vehicle;
  const p = (f.profiles && f.profiles[key]) || VEHICLE_PRESETS[key] || VEHICLE_PRESETS.hiace;
  const totalVans = n(f.vans) + n(f.spareVans);
  const daysPerVan = n(model.ops.weekdayDays) + n(model.ops.saturdayDays);
  const kmPerVanYear = n(model.fuel.kmPerDay) * daysPerVan;

  const residual = n(p.capex) * pc(p.residualPct);
  const depreciation = n(f.termYears) > 0 ? (n(p.capex) - residual) / n(f.termYears) : 0;
  const interest = ((n(p.capex) + residual) / 2) * pc(f.financeRatePct);
  const ownership = f.ownership === 'lease'
    ? n(f.leasePerVanPerMonth) * 12
    : depreciation + interest;

  // Servicing is whichever comes first — distance or time. At 25 km a day the CBD
  // fleet is always on the time interval, which is why the km figure alone would
  // badly under-count the servicing bill.
  const servicesByKm = n(p.serviceIntervalKm) > 0 ? kmPerVanYear / n(p.serviceIntervalKm) : 0;
  const servicesByTime = n(p.serviceIntervalMonths) > 0 ? 12 / n(p.serviceIntervalMonths) : 0;
  const servicesPerYear = Math.max(servicesByKm, servicesByTime);
  const servicing = servicesPerYear * n(p.serviceCost);
  const tyres = n(p.tyreLifeKm) > 0 ? (kmPerVanYear / n(p.tyreLifeKm)) * n(p.tyreSetCost) : 0;

  const perVan = {
    ownership, depreciation, interest, residual,
    regoCtp: n(p.regoCtp),
    insurance: n(p.insurance),
    servicing, servicesPerYear,
    tyres,
    repairs: n(p.repairs),
  };
  perVan.total = perVan.ownership + perVan.regoCtp + perVan.insurance + perVan.servicing + perVan.tyres + perVan.repairs;

  return { key, profile: p, totalVans, kmPerVanYear, daysPerVan, perVan, total: perVan.total * totalVans };
}

// ── Fuel ─────────────────────────────────────────────────────────────────────
// The ADR combined figure is a highway-ish average. A CBD delivery van never gets
// there: it stops every hundred metres, restarts cold, crawls in traffic and idles
// at the kerb. So the model states the ADR number, applies an explicit stop-start
// uplift to it, and then adds idling separately — idling burns litres at zero km,
// so it can never be expressed as an L/100 km figure at all.
export function computeFuel(model, vehicleKey) {
  const fu = model.fuel;
  const f = model.fleet;
  const key = vehicleKey || f.vehicle;
  const p = (f.profiles && f.profiles[key]) || VEHICLE_PRESETS[key];
  const daysPerVan = n(model.ops.weekdayDays) + n(model.ops.saturdayDays);
  const vans = n(f.vans);            // spares burn no fuel — they are not on the road

  const adr = n(p.l100);
  const effective = adr * (1 + pc(fu.stopStartUpliftPct));
  const drivingLitresPerDay = n(fu.kmPerDay) * effective / 100;
  const idleLitresPerDay = n(fu.idleHoursPerDay) * n(fu.idleLitresPerHour);
  const litresPerDay = drivingLitresPerDay + idleLitresPerDay;
  // What the fleet's real-world consumption works out at once idling is folded
  // back into the distance — the number to sanity-check against fuel-card data.
  const allInL100 = n(fu.kmPerDay) > 0 ? (litresPerDay / n(fu.kmPerDay)) * 100 : 0;

  const costPerDay = litresPerDay * n(fu.dieselPerLitre);
  const litresPerYear = litresPerDay * daysPerVan * vans;
  return {
    adr, effective, allInL100,
    drivingLitresPerDay, idleLitresPerDay, litresPerDay,
    costPerDay, costPerVanYear: costPerDay * daysPerVan,
    litresPerYear, total: costPerDay * daysPerVan * vans,
  };
}

// ── Equipment ────────────────────────────────────────────────────────────────
export function computeEquipment(model, labour) {
  const e = model.equipment;
  const drivers = Math.ceil(n(model.ops.driversPerDay));
  const vans = n(model.fleet.vans) + n(model.fleet.spareVans);
  const driverDays = labour.driverDays;

  const mobiles = drivers * n(e.mobilePerMonth) * 12;
  const trolleys = n(e.trolleyLifeYears) > 0 ? (drivers * n(e.trolleyCost)) / n(e.trolleyLifeYears) : 0;
  const parkingTolls = driverDays * n(e.parkingTollsPerDay);
  const fines = vans * n(e.finesPerVanPerYear);
  const rows = (e.rows || []).map((r) => ({ label: r.label, total: drivers * n(r.perDriverPerYear) }));
  const other = rows.reduce((s, r) => s + r.total, 0);

  return { drivers, mobiles, trolleys, parkingTolls, fines, rows, other,
    total: mobiles + trolleys + parkingTolls + fines + other };
}

// ── Cost base ────────────────────────────────────────────────────────────────
export function computeCosts(model, vehicleKey) {
  const labour = computeLabour(model);
  const vehicles = computeVehicle(model, vehicleKey);
  const fuel = computeFuel(model, vehicleKey);
  const equipment = computeEquipment(model, labour);
  const overheadRows = (model.overheads.rows || []).map((r) => ({ label: r.label, annual: n(r.annual) }));
  const overheads = overheadRows.reduce((s, r) => s + r.annual, 0);

  const direct = labour.total + vehicles.total + fuel.total + equipment.total;
  const total = direct + overheads;

  return {
    labour, vehicles, fuel, equipment,
    overheads: { rows: overheadRows, total: overheads },
    direct, total,
    perDriverDay: labour.driverDays ? total / labour.driverDays : 0,
    groups: [
      { key: 'labour',    label: 'Driver labour & on-costs', total: labour.total },
      { key: 'vehicles',  label: 'Vehicles',                 total: vehicles.total },
      { key: 'fuel',      label: 'Fuel',                     total: fuel.total },
      { key: 'equipment', label: 'Equipment & road costs',   total: equipment.total },
      { key: 'overheads', label: 'Indirect / overhead',      total: overheads },
    ],
  };
}

// ── Volumes ──────────────────────────────────────────────────────────────────
// Annual units per line. Parcels, depot bags and Express are per operating day;
// DoE is already an annual count of service days.
export function annualVolumes(model) {
  const days = n(model.ops.weekdayDays) + n(model.ops.saturdayDays);
  const L = model.lines;
  return {
    days,
    parcels: n(L.parcels.volPerYear),
    depot: n(L.depot.volPerYear),
    doe: n(L.doe.volPerYear),
    express: n(L.express.volPerYear),
    // Per delivery day, for reading against what the depot sees on a Tuesday.
    perDay: days > 0 ? {
      parcels: n(L.parcels.volPerYear) / days,
      depot: n(L.depot.volPerYear) / days,
      express: n(L.express.volPerYear) / days,
    } : { parcels: 0, depot: 0, express: 0 },
  };
}

// Revenue a set of rates would produce on those volumes. DoE carries its per-school
// collections alongside the day rate; they ride at the same uplift as the day rate.
export function revenueAt(model, rateOf) {
  const v = annualVolumes(model);
  const L = model.lines;
  const doeUplift = n(L.doe.currentRate) > 0 ? rateOf('doe') / n(L.doe.currentRate) : 1;
  const collections = n(L.doe.collectionsPerYear) * n(L.doe.collectionRate) * doeUplift;
  const per = {
    parcels: L.parcels.include === false ? 0 : v.parcels * rateOf('parcels'),
    depot:   L.depot.include === false   ? 0 : v.depot * rateOf('depot'),
    doe:     L.doe.include === false     ? 0 : v.doe * rateOf('doe') + collections,
    express: L.express.include === false ? 0 : v.express * rateOf('express'),
  };
  return { per, collections, total: per.parcels + per.depot + per.doe + per.express };
}

// ── Pricing ──────────────────────────────────────────────────────────────────
// Solve for the rates that deliver the target margin.
//
// Required revenue = cost / (1 − margin). Each included line is given a share of
// that revenue in proportion to volume × weight, and the rate falls out of the
// share. Lines held at a floor (never price below what AP pays today) are fixed
// first and the rest of the revenue is re-spread across the lines still free to
// move — repeated until nothing new hits its floor, because fixing one line pushes
// the others up and can push a second line off its floor in turn.
export function solvePrices(model, costTotal) {
  const v = annualVolumes(model);
  const L = model.lines;
  const keys = LINE_DEFS.map((d) => d.key).filter((k) => L[k].include !== false);
  const margin = model.margin.basis === 'cost' ? 0 : pc(model.margin.pct);
  const required = model.margin.basis === 'cost'
    ? costTotal * (1 + pc(model.margin.pct))
    : (margin >= 1 ? Infinity : costTotal / (1 - margin));

  const volOf = (k) => (k === 'doe' ? v.doe : v[k]);
  // DoE's collections revenue scales with its day rate, so a dollar of DoE day rate
  // brings in more than the day count alone suggests. Fold that in as extra units.
  const unitsOf = (k) => {
    if (k !== 'doe') return volOf(k);
    const dayRate = n(L.doe.currentRate);
    const extra = dayRate > 0 ? (n(L.doe.collectionsPerYear) * n(L.doe.collectionRate)) / dayRate : 0;
    return volOf(k) + extra;
  };

  const fixed = {};                       // key → rate held at its floor
  const rate = {};
  for (let pass = 0; pass < keys.length + 1; pass++) {
    const freeKeys = keys.filter((k) => !(k in fixed));
    const fixedRevenue = Object.keys(fixed).reduce((s, k) => s + unitsOf(k) * fixed[k], 0);
    const remaining = required - fixedRevenue;
    const weighted = freeKeys.reduce((s, k) => s + unitsOf(k) * n(L[k].weight), 0);
    const perWeightedUnit = weighted > 0 ? remaining / weighted : 0;

    let hitFloor = false;
    for (const k of freeKeys) {
      const r = perWeightedUnit * n(L[k].weight);
      if (L[k].floorAtCurrent && r < n(L[k].currentRate)) { fixed[k] = n(L[k].currentRate); hitFloor = true; }
      else rate[k] = r;
    }
    for (const k of Object.keys(fixed)) rate[k] = fixed[k];
    if (!hitFloor) break;
  }
  for (const k of LINE_DEFS.map((d) => d.key)) if (!(k in rate)) rate[k] = n(L[k].currentRate);

  const proposed = revenueAt(model, (k) => rate[k]);
  const current = revenueAt(model, (k) => n(L[k].currentRate));

  const lines = LINE_DEFS.map((d) => {
    const cfg = L[d.key];
    const cur = n(cfg.currentRate);
    const prop = rate[d.key];
    return {
      ...d,
      include: cfg.include !== false,
      volume: volOf(d.key),
      currentRate: cur,
      proposedRate: prop,
      upliftPct: cur > 0 ? (prop / cur - 1) * 100 : 0,
      atFloor: d.key in fixed,
      currentRevenue: current.per[d.key],
      proposedRevenue: proposed.per[d.key],
    };
  });

  return {
    required, lines,
    currentRevenue: current.total,
    proposedRevenue: proposed.total,
    // Shortfall the uplift has to close: what today's rates leave on the table.
    gap: proposed.total - current.total,
    // How the model performs if AP simply rolls the current rates over.
    currentMarginPct: current.total > 0 ? ((current.total - costTotal) / current.total) * 100 : 0,
    currentProfit: current.total - costTotal,
    proposedMarginPct: proposed.total > 0 ? ((proposed.total - costTotal) / proposed.total) * 100 : 0,
    proposedProfit: proposed.total - costTotal,
    // One number for the meeting: the average uplift across the whole submission.
    blendedUpliftPct: current.total > 0 ? (proposed.total / current.total - 1) * 100 : 0,
    // If today's rates already clear the target, the answer is not "0% uplift" —
    // it is how much slack there is. Positive headroom is revenue above what the
    // margin needs; costHeadroom is the extra annual cost that slack would absorb
    // before the contract dips under target.
    headroom: current.total - required,
    costHeadroom: current.total * (1 - margin) - costTotal,
  };
}

// ── The whole run ────────────────────────────────────────────────────────────
export function runModel(model, vehicleKey) {
  const costs = computeCosts(model, vehicleKey);
  const pricing = solvePrices(model, costs.total);
  const vols = annualVolumes(model);
  const parcelUnits = vols.parcels + vols.express;
  const other = n(model.otherRevenue && model.otherRevenue.annual);
  return {
    vehicleKey: vehicleKey || model.fleet.vehicle,
    costs, pricing, volumes: vols,
    costPerParcel: parcelUnits > 0 ? costs.total / parcelUnits : 0,
    costPerDriverDay: costs.perDriverDay,
    // The four priced lines plus whatever else the company invoices — the figure to
    // hold against the actual turnover, since only the four are modelled here.
    turnover: { priced: pricing.currentRevenue, other, total: pricing.currentRevenue + other },
  };
}

// Both vehicle options run through the same model, so the comparison is like for
// like and the only thing that changes is the van.
export function compareVehicles(model) {
  const out = {};
  for (const key of Object.keys(model.fleet.profiles || VEHICLE_PRESETS)) out[key] = runModel(model, key);
  return out;
}
